import * as THREE from 'three';
import { createActorMesh, type ActorMeshBundle } from '../renderer/3d/assets/actors';
import { createWindowFrame, windowId } from './windowing';

export interface SelfCharacterProfile {
  actorId: string;
  playerId?: string;
  name: string;
  guildName?: string;
  hp?: number;
  maxHp?: number;
  entityType: string;
  color: string;
  accessoriesJson: string;
}

export interface SelfCharacterWindowHandle {
  show(): void;
  hide(): void;
  isOpen(): boolean;
  refresh(): void;
  destroy(): void;
}

interface SelfCharacterWindowOptions {
  getProfile: () => SelfCharacterProfile;
}

export function createSelfCharacterWindow(options: SelfCharacterWindowOptions): SelfCharacterWindowHandle {
  const initialSize = {
    width: Math.max(340, Math.round(window.innerWidth * 0.22)),
    height: Math.max(460, Math.round(window.innerHeight * 0.58)),
  };
  const initialPosition = {
    x: Math.max(24, Math.round(window.innerWidth * 0.06)),
    y: Math.max(72, Math.round(window.innerHeight * 0.1)),
  };

  const container = document.createElement('div');
  container.className = 'window-container self-character-window';
  Object.assign(container.style, {
    position: 'fixed',
    left: `${initialPosition.x}px`,
    top: `${initialPosition.y}px`,
    width: `${initialSize.width}px`,
    height: `${initialSize.height}px`,
    minHeight: '0',
    overflow: 'hidden',
    display: 'none',
    flexDirection: 'column',
  });

  const header = document.createElement('div');
  header.className = 'window-header';

  const title = document.createElement('div');
  title.className = 'window-header-title';
  title.textContent = 'Character';

  const resizeHandle = document.createElement('div');
  resizeHandle.className = 'window-resize-handle';
  resizeHandle.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" style="pointer-events: none;"><path d="M0 0 L10 0 L10 10 Z" fill="#9ca3af" /></svg>';

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.textContent = '×';
  Object.assign(closeButton.style, {
    marginLeft: 'auto',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#9ca3af',
    fontSize: '1.5rem',
    cursor: 'pointer',
    lineHeight: '1',
    zIndex: '20',
  });

  header.append(title, resizeHandle, closeButton);

  const content = document.createElement('div');
  content.className = 'window-content';
  Object.assign(content.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: '1 1 auto',
    minHeight: '0',
    padding: '12px',
  });

  const previewFrame = document.createElement('div');
  Object.assign(previewFrame.style, {
    position: 'relative',
    flex: '0 0 260px',
    minHeight: '220px',
    borderRadius: '12px',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    background:
      'radial-gradient(circle at top, rgba(56, 189, 248, 0.18), transparent 45%), linear-gradient(180deg, rgba(15, 23, 42, 0.96), rgba(2, 6, 23, 0.98))',
    overflow: 'hidden',
    cursor: 'grab',
    touchAction: 'none',
  });

  const previewHint = document.createElement('div');
  previewHint.textContent = 'Drag to rotate';
  Object.assign(previewHint.style, {
    position: 'absolute',
    right: '10px',
    bottom: '10px',
    zIndex: '2',
    fontSize: '0.72rem',
    color: 'rgba(226, 232, 240, 0.85)',
    background: 'rgba(15, 23, 42, 0.55)',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    borderRadius: '999px',
    padding: '4px 8px',
    pointerEvents: 'none',
  });

  const previewCanvas = document.createElement('canvas');
  Object.assign(previewCanvas.style, {
    display: 'block',
    width: '100%',
    height: '100%',
  });
  previewFrame.append(previewCanvas, previewHint);

  const stats = document.createElement('div');
  Object.assign(stats.style, {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '8px',
    fontSize: '0.86rem',
    color: '#e2e8f0',
  });

  const nameEl = document.createElement('div');
  Object.assign(nameEl.style, {
    fontSize: '1.05rem',
    fontWeight: '700',
    color: '#f8fafc',
  });
  const guildEl = makeStatLine();
  const hpEl = makeStatLine();
  const appearanceEl = makeStatLine();
  const idEl = makeStatLine();

  stats.append(nameEl, guildEl, hpEl, appearanceEl, idEl);
  content.append(previewFrame, stats);
  container.append(header, content);
  document.body.appendChild(container);

  const windowFrame = createWindowFrame({
    windowId: windowId('self-character', 'main'),
    container,
    header,
    resizeHandle,
    closeButton,
    minWidth: 320,
    minHeight: 380,
    initialPosition,
    initialSize,
  });
  windowFrame.setActiveTab('main');

  let isOpen = false;
  if (windowFrame.getWindowState() === 'open') {
    isOpen = true;
    container.style.display = 'flex';
  }

  const renderer = new THREE.WebGLRenderer({ canvas: previewCanvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 100);
  camera.position.set(0, 0.9, 4.4);
  camera.lookAt(0, 0.7, 0);

  const ambient = new THREE.AmbientLight('#dbeafe', 2.4);
  const keyLight = new THREE.DirectionalLight('#fff7ed', 2.2);
  keyLight.position.set(2.8, 4.2, 3.8);
  const rimLight = new THREE.DirectionalLight('#86efac', 0.8);
  rimLight.position.set(-2.5, 2.6, -3.4);
  scene.add(ambient, keyLight, rimLight);

  const modelRoot = new THREE.Group();
  scene.add(modelRoot);

  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(1.22, 40),
    new THREE.MeshBasicMaterial({
      color: '#d9f99d',
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
    }),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -0.48;
  scene.add(floor);

  let bundle: ActorMeshBundle | null = null;
  let currentSignature = '';
  let dragPointerId: number | null = null;
  let dragMode: 'rotate' | 'translate' | null = null;
  let lastPointerX = 0;
  let lastPointerY = 0;
  let modelYaw = Math.PI / 5;
  let modelPitch = -0.08;
  let modelOffsetX = 0;
  let modelOffsetY = 0;

  const resizeObserver = new ResizeObserver(() => {
    resizePreview();
    renderPreview();
  });
  resizeObserver.observe(previewFrame);

  function show(): void {
    if (isOpen) {
      refresh();
      return;
    }
    isOpen = true;
    container.style.display = 'flex';
    windowFrame.setWindowState('open');
    refresh();
  }

  function hide(): void {
    if (!isOpen) return;
    isOpen = false;
    container.style.display = 'none';
    windowFrame.setWindowState('closed');
  }

  function refresh(): void {
    const profile = options.getProfile();
    nameEl.textContent = profile.name;
    guildEl.textContent = profile.guildName?.trim() ? `Guild: ${profile.guildName}` : 'Guild: None';
    hpEl.textContent =
      typeof profile.hp === 'number'
        ? `HP: ${Math.round(profile.hp)}${typeof profile.maxHp === 'number' ? ` / ${Math.round(profile.maxHp)}` : ''}`
        : 'HP: Unknown';
    appearanceEl.textContent = `Appearance: ${formatEntityType(profile.entityType)}`;
    idEl.textContent = `Entity ID: ${profile.actorId}`;

    const nextSignature = `${profile.entityType}|${profile.color}|${profile.accessoriesJson}`;
    if (nextSignature !== currentSignature) {
      currentSignature = nextSignature;
      rebuildModel(profile);
    }
    resizePreview();
    renderPreview();
  }

  function resizePreview(): void {
    const width = Math.max(1, Math.floor(previewFrame.clientWidth));
    const height = Math.max(1, Math.floor(previewFrame.clientHeight));
    if (previewCanvas.width !== width || previewCanvas.height !== height) {
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    }
  }

  function rebuildModel(profile: SelfCharacterProfile): void {
    if (bundle) {
      modelRoot.remove(bundle.group);
      disposeObject3D(bundle.group);
      bundle = null;
    }
    bundle = createActorMesh({
      kind: profile.entityType.toLowerCase() === 'player' ? 'self' : 'mob',
      entityType: profile.entityType,
      color: profile.color,
      accessoriesJson: profile.accessoriesJson,
    });
    bundle.group.position.set(0, -0.42, 0);
    modelRoot.add(bundle.group);
  }

  function renderPreview(): void {
    if (!isOpen) {
      return;
    }
    modelRoot.position.set(modelOffsetX, modelOffsetY, 0);
    modelRoot.rotation.y = modelYaw;
    modelRoot.rotation.x = modelPitch;
    renderer.render(scene, camera);
  }

  function onPreviewPointerDown(event: PointerEvent): void {
    if (event.button !== 0 && event.button !== 1) return;
    dragPointerId = event.pointerId;
    dragMode = event.button === 1 ? 'translate' : 'rotate';
    lastPointerX = event.clientX;
    lastPointerY = event.clientY;
    previewFrame.style.cursor = 'grabbing';
    previewFrame.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  function onPreviewPointerMove(event: PointerEvent): void {
    if (event.pointerId !== dragPointerId) return;
    const dx = event.clientX - lastPointerX;
    const dy = event.clientY - lastPointerY;
    lastPointerX = event.clientX;
    lastPointerY = event.clientY;
    if (dragMode === 'translate') {
      modelOffsetX = THREE.MathUtils.clamp(modelOffsetX + dx * 0.01, -1.4, 1.4);
      modelOffsetY = THREE.MathUtils.clamp(modelOffsetY - dy * 0.01, -1.4, 1.2);
    } else {
      modelYaw += dx * 0.015;
      modelPitch = THREE.MathUtils.clamp(modelPitch + dy * 0.006, -0.45, 0.2);
    }
    renderPreview();
  }

  function endPreviewDrag(event: PointerEvent): void {
    if (event.pointerId !== dragPointerId) return;
    dragPointerId = null;
    dragMode = null;
    previewFrame.style.cursor = 'grab';
    if (previewFrame.hasPointerCapture(event.pointerId)) {
      previewFrame.releasePointerCapture(event.pointerId);
    }
  }

  closeButton.addEventListener('click', hide);
  previewFrame.addEventListener('pointerdown', onPreviewPointerDown);
  previewFrame.addEventListener('pointermove', onPreviewPointerMove);
  previewFrame.addEventListener('pointerup', endPreviewDrag);
  previewFrame.addEventListener('pointercancel', endPreviewDrag);
  previewFrame.addEventListener('auxclick', (event) => event.preventDefault());
  previewFrame.addEventListener('contextmenu', (event) => event.preventDefault());

  if (isOpen) {
    refresh();
  }

  return {
    show,
    hide,
    isOpen: () => isOpen,
    refresh,
    destroy(): void {
      resizeObserver.disconnect();
      closeButton.removeEventListener('click', hide);
      previewFrame.removeEventListener('pointerdown', onPreviewPointerDown);
      previewFrame.removeEventListener('pointermove', onPreviewPointerMove);
      previewFrame.removeEventListener('pointerup', endPreviewDrag);
      previewFrame.removeEventListener('pointercancel', endPreviewDrag);
      if (bundle) {
        modelRoot.remove(bundle.group);
        disposeObject3D(bundle.group);
        bundle = null;
      }
      disposeObject3D(floor);
      renderer.dispose();
      windowFrame.destroy();
      container.remove();
    },
  };
}

function makeStatLine(): HTMLDivElement {
  const line = document.createElement('div');
  Object.assign(line.style, {
    color: '#cbd5e1',
    minWidth: '0',
    wordBreak: 'break-word',
  });
  return line;
}

function formatEntityType(entityType: string): string {
  const normalized = entityType.trim();
  if (!normalized) {
    return 'Player';
  }
  return normalized
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function disposeObject3D(object: THREE.Object3D): void {
  const disposedMaterials = new Set<THREE.Material>();
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }
    const material = mesh.material;
    if (Array.isArray(material)) {
      for (const entry of material) {
        if (!disposedMaterials.has(entry)) {
          entry.dispose();
          disposedMaterials.add(entry);
        }
      }
      return;
    }
    if (material && !disposedMaterials.has(material)) {
      material.dispose();
      disposedMaterials.add(material);
    }
  });
}
